import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScanLine, Loader2, Sparkles } from "lucide-react";
import { TESLA_COLORS } from "@/lib/teslaColors";
import { toKm, fromKm, unitLabel } from "@/lib/units";
import { api } from "@/lib/api";
import { toast } from "sonner";
import VehiclePhotoPicker from "@/components/VehiclePhotoPicker";

const WHEEL_SIZES = ['18"', '19"', '20"', '21"'];

const STEPS = ["Vehicle", "Insurance", "Review"];

export default function AddVehicleWizard({ open, onOpenChange, unit, onCreate }) {
  const [step, setStep] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState(null);

  useEffect(() => {
    if (open) {
      setStep(0);
      setForm({
        name: "",
        make: "Tesla",
        model: "",
        year: new Date().getFullYear(),
        vin: "",
        wheel_size: "",
        paint_name: "",
        image_url: "",
        energy_type: "ev",
        usable_battery_kwh: "75",
        fuel_tank_capacity_l: "",
        yearly_limit_display: unit === "mi" ? "12500" : "20000",
        insurance_company: "",
        policy_number: "",
        policy_start_date: "",
      });
    }
  }, [open, unit]);

  if (!form) return null;

  const handleScan = async (file) => {
    setScanning(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await api.post("/insurance/scan", {
        image_base64: dataUrl,
        mime_type: file.type || "image/jpeg",
      });
      const d = res.data;
      const allowanceKm = d.annual_allowance_km || 0;
      setForm((prev) => ({
        ...prev,
        insurance_company: d.insurance_company || prev.insurance_company,
        policy_number: d.policy_number || prev.policy_number,
        policy_start_date: d.policy_start_date || prev.policy_start_date,
        yearly_limit_display:
          allowanceKm > 0
            ? fromKm(allowanceKm, unit).toFixed(0)
            : prev.yearly_limit_display,
      }));
      toast.success("Insurance scanned — fields pre-populated. Review before saving.");
    } catch (e) {
      toast.error("Scan failed. Enter details manually.");
    } finally {
      setScanning(false);
    }
  };

  const submit = async () => {
    if (!form.name) {
      toast.error("Vehicle name is required");
      setStep(0);
      return;
    }
    setSubmitting(true);
    try {
      const paint = TESLA_COLORS.find((c) => c.name === form.paint_name);
      await onCreate({
        name: form.name,
        make: form.make,
        model: form.model,
        year: form.year ? parseInt(form.year) : null,
        vin: form.vin,
        wheel_size: form.wheel_size,
        paint_name: form.paint_name,
        color: paint?.hex || "#FFFFFF",
        image_url: form.image_url,
        energy_type: form.energy_type,
        usable_battery_kwh:
          form.usable_battery_kwh !== "" ? parseFloat(form.usable_battery_kwh) : null,
        fuel_tank_capacity_l:
          form.fuel_tank_capacity_l !== "" ? parseFloat(form.fuel_tank_capacity_l) : null,
        yearly_limit_km: toKm(parseFloat(form.yearly_limit_display) || 0, unit),
        insurance_company: form.insurance_company,
        policy_number: form.policy_number,
        policy_start_date: form.policy_start_date,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-deep)] border-[var(--border-hover)] text-[var(--text-primary)] sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
        style={{ borderRadius: 4 }}
        data-testid="add-vehicle-wizard"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tighter">
            Add Vehicle
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">
            Step {step + 1} of {STEPS.length} · {STEPS[step]}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex gap-1 mb-2">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className="flex-1 h-0.5 transition-colors"
              style={{ background: i <= step ? "#FFFFFF" : "#1a1a1a" }}
            />
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4" data-testid="wizard-step-vehicle">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2">
                <Label className="label-tiny">Display Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Daily Driver"
                  className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
                  data-testid="wizard-name"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-tiny">Make</Label>
                <Input
                  value={form.make}
                  onChange={(e) => setForm({ ...form, make: e.target.value })}
                  className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
                  data-testid="wizard-make"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-tiny">Model</Label>
                <Input
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  placeholder="Model 3"
                  className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
                  data-testid="wizard-model"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-tiny">Year</Label>
                <Input
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                  className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                  data-testid="wizard-year"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-tiny">Wheel Size</Label>
                <Select value={form.wheel_size} onValueChange={(v) => setForm({ ...form, wheel_size: v })}>
                  <SelectTrigger
                    className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                    data-testid="wizard-wheel-size"
                  >
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]">
                    {WHEEL_SIZES.map((w) => (
                      <SelectItem key={w} value={w}>{w}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="label-tiny">VIN</Label>
                <Input
                  value={form.vin}
                  onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })}
                  placeholder="17-character VIN"
                  maxLength={17}
                  className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data tracking-wider"
                  data-testid="wizard-vin"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="label-tiny">Paint Color</Label>
                <Select value={form.paint_name} onValueChange={(v) => setForm({ ...form, paint_name: v })}>
                  <SelectTrigger
                    className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
                    data-testid="wizard-paint"
                  >
                    <SelectValue placeholder="Select color" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] max-h-[280px]">
                    {TESLA_COLORS.map((c) => (
                      <SelectItem key={c.code} value={c.name}>
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 border border-[#333]" style={{ background: c.hex, borderRadius: 1 }} />
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="label-tiny">Energy Type</Label>
                <div
                  className="inline-flex items-center bg-[var(--bg-canvas)] border border-[var(--border-hover)] p-0.5"
                  style={{ borderRadius: 2 }}
                  data-testid="wizard-energy-type"
                >
                  {[
                    ["ev", "EV"],
                    ["gas", "Gas"],
                    ["hybrid", "Hybrid"],
                  ].map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setForm({ ...form, energy_type: val })}
                      data-testid={`wizard-energy-${val}`}
                      className={`tap px-3 py-1 text-xs tracking-wider transition-colors ${
                        form.energy_type === val ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                      }`}
                      style={{ borderRadius: 1 }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {(form.energy_type === "ev" || form.energy_type === "hybrid") && (
                <div className="space-y-1 col-span-2 sm:col-span-1">
                  <Label className="label-tiny">Usable Battery (kWh)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={form.usable_battery_kwh}
                    onChange={(e) => setForm({ ...form, usable_battery_kwh: e.target.value })}
                    placeholder="e.g. 75"
                    className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                    data-testid="wizard-battery-kwh"
                  />
                </div>
              )}
              {(form.energy_type === "gas" || form.energy_type === "hybrid") && (
                <div className="space-y-1 col-span-2 sm:col-span-1">
                  <Label className="label-tiny">Fuel Tank (L) · optional</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={form.fuel_tank_capacity_l}
                    onChange={(e) => setForm({ ...form, fuel_tank_capacity_l: e.target.value })}
                    placeholder="—"
                    className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                    data-testid="wizard-fuel-tank"
                  />
                </div>
              )}
              <div className="space-y-1 col-span-2">
                <Label className="label-tiny">Photo (optional)</Label>
                <VehiclePhotoPicker
                  value={form.image_url}
                  onChange={(url) => setForm({ ...form, image_url: url })}
                  testIdPrefix="wizard-photo"
                />
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-insurance">
            {/* Scan card */}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={scanning}
              className="w-full bg-[var(--bg-canvas)] border border-dashed border-[var(--border-subtle)] hover:border-white/30 transition-colors p-6 flex items-center gap-4 text-left disabled:opacity-50"
              style={{ borderRadius: 2 }}
              data-testid="wizard-scan-btn"
            >
              <div className="w-12 h-12 bg-[var(--bg-inverse)] text-[var(--text-inverse)] flex items-center justify-center" style={{ borderRadius: 2 }}>
                {scanning ? (
                  <Loader2 className="w-6 h-6 animate-spin" strokeWidth={1.75} />
                ) : (
                  <ScanLine className="w-6 h-6" strokeWidth={1.75} />
                )}
              </div>
              <div className="flex-1">
                <div className="font-display text-base text-[var(--text-primary)] flex items-center gap-2">
                  Scan Insurance Document
                  <Sparkles className="w-3.5 h-3.5 text-[var(--text-primary)]" strokeWidth={1.5} />
                </div>
                <div className="text-xs text-[var(--text-muted)] mt-0.5">
                  {scanning
                    ? "Reading document..."
                    : "Upload a photo or scan; AI will pre-populate fields"}
                </div>
              </div>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleScan(f);
                e.target.value = "";
              }}
              data-testid="wizard-scan-file-input"
            />

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2">
                <Label className="label-tiny">Insurance Company</Label>
                <Input
                  value={form.insurance_company}
                  onChange={(e) => setForm({ ...form, insurance_company: e.target.value })}
                  className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
                  data-testid="wizard-ins-company"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-tiny">Policy Number</Label>
                <Input
                  value={form.policy_number}
                  onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                  className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                  data-testid="wizard-policy-number"
                />
              </div>
              <div className="space-y-1">
                <Label className="label-tiny">Policy Start</Label>
                <Input
                  type="date"
                  value={form.policy_start_date}
                  onChange={(e) => setForm({ ...form, policy_start_date: e.target.value })}
                  className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                  data-testid="wizard-policy-start"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="label-tiny">Annual Allowance ({unitLabel(unit)})</Label>
                <Input
                  type="number"
                  value={form.yearly_limit_display}
                  onChange={(e) => setForm({ ...form, yearly_limit_display: e.target.value })}
                  className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
                  data-testid="wizard-allowance"
                />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-review">
            <div className="bg-[var(--bg-canvas)] border border-[var(--border-hover)] p-5" style={{ borderRadius: 2 }}>
              <div className="label-tiny mb-3">Vehicle</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[var(--text-muted)]">Name</span><div className="text-[var(--text-primary)]">{form.name || "—"}</div></div>
                <div><span className="text-[var(--text-muted)]">Year/Make/Model</span><div className="text-[var(--text-primary)]">{[form.year, form.make, form.model].filter(Boolean).join(" ") || "—"}</div></div>
                <div><span className="text-[var(--text-muted)]">VIN</span><div className="text-[var(--text-primary)] font-mono-data text-xs">{form.vin || "—"}</div></div>
                <div><span className="text-[var(--text-muted)]">Wheel</span><div className="text-[var(--text-primary)] font-mono-data">{form.wheel_size || "—"}</div></div>
                <div className="col-span-2"><span className="text-[var(--text-muted)]">Paint</span><div className="text-[var(--text-primary)]">{form.paint_name || "—"}</div></div>
              </div>
            </div>
            <div className="bg-[var(--bg-canvas)] border border-[var(--border-hover)] p-5" style={{ borderRadius: 2 }}>
              <div className="label-tiny mb-3">Insurance</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2"><span className="text-[var(--text-muted)]">Company</span><div className="text-[var(--text-primary)]">{form.insurance_company || "—"}</div></div>
                <div><span className="text-[var(--text-muted)]">Policy #</span><div className="text-[var(--text-primary)] font-mono-data text-xs">{form.policy_number || "—"}</div></div>
                <div><span className="text-[var(--text-muted)]">Start</span><div className="text-[var(--text-primary)] font-mono-data">{form.policy_start_date || "—"}</div></div>
                <div className="col-span-2"><span className="text-[var(--text-muted)]">Annual Allowance</span><div className="text-[var(--text-primary)] font-mono-data">{form.yearly_limit_display} {unitLabel(unit)}</div></div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex justify-between sm:justify-between">
          {step > 0 ? (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="bg-transparent border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-emphasis)]"
              style={{ borderRadius: 2 }}
              data-testid="wizard-back-btn"
            >
              Back
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-transparent border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-emphasis)]"
              style={{ borderRadius: 2 }}
              data-testid="wizard-cancel-btn"
            >
              Cancel
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={step === 0 && !form.name}
              className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
              style={{ borderRadius: 2 }}
              data-testid="wizard-next-btn"
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
              style={{ borderRadius: 2 }}
              data-testid="wizard-submit-btn"
            >
              {submitting ? "Saving..." : "Add Vehicle"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
